/**
 * Manages audio chunk storage and upload.
 *
 * Responsibilities:
 * - Accumulate MediaRecorder blobs in chunksRef
 * - Persist chunks to IndexedDB every 5 seconds via startPeriodicSave
 * - Upload delta chunks to the server on a timer via startChunkUploadTimer
 * - Retry failed uploads with exponential backoff (1s / 3s / 10s)
 * - Expose doChunkUpload for on-demand uploads (recording start/finish)
 */

import type { TStreamChannel } from "@lexicanon/shared"
import { useCallback, useEffect, useRef, useState } from "react"
import { uploadChunk } from "../lib/api"
import { extractErrorMessage } from "../lib/api/errors"
import { DEFAULT_RECORDER_MIME } from "../lib/recorder-mime"
import { saveChunksToIDB } from "./useIndexedDB"

const CHUNK_UPLOAD_INTERVAL_MS = 15_000

interface IProps {
  meetingId: string
  subject: string | undefined
  templateId: string | undefined
  isOnlineRef: React.MutableRefObject<boolean>
  provisionalPendingRef: React.MutableRefObject<boolean>
  startTimeRef: React.MutableRefObject<number>
  reportSuccess: () => void
  reportFailure: () => void
  setError: (err: string) => void
  /**
   * Which channel this instance uploads. "room" (default) is the mic; "remote"
   * is the desktop system-loopback stream. A separate instance per channel keeps
   * the buffer/cursor/timers fully independent — the room path is unchanged.
   */
  channel?: TStreamChannel
  /** Remote-only: capture-time offset (seconds) sent with the first chunk. */
  offsetSecRef?: React.MutableRefObject<number | undefined>
}

interface IUseChunkUploadResult {
  chunksRef: React.MutableRefObject<Blob[]>
  activeMimeTypeRef: React.MutableRefObject<string>
  uploadingRef: React.MutableRefObject<boolean>
  lastUploadedChunkIndexRef: React.MutableRefObject<number>
  uploadBatchIndexRef: React.MutableRefObject<number>
  /** Absolute sequence number of chunksRef[0] — how many chunks have been
   *  trimmed from the front after successful uploads. IDB persistence needs it
   *  to keep the complete recording (the "client gold copy") while RAM only
   *  holds the un-uploaded tail. */
  absoluteBaseRef: React.MutableRefObject<number>
  uploading: boolean
  uploadCount: number
  setUploadCount: React.Dispatch<React.SetStateAction<number>>
  idbWarning: boolean
  setIdbWarning: React.Dispatch<React.SetStateAction<boolean>>
  doChunkUpload: (isFinal: boolean) => Promise<boolean>
  startPeriodicSave: () => void
  stopPeriodicSave: () => void
  startChunkUploadTimer: () => void
  stopChunkUploadTimer: () => void
}

export function useChunkUpload({
  meetingId,
  subject,
  templateId,
  isOnlineRef,
  provisionalPendingRef,
  startTimeRef,
  reportSuccess,
  reportFailure,
  setError,
  channel = "room",
  offsetSecRef,
}: IProps): IUseChunkUploadResult {
  // Internal refs
  const uploadLockRef = useRef<Promise<boolean>>(Promise.resolve(true))
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const chunkUploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const idbOkRef = useRef(true)

  // Exposed refs
  const chunksRef = useRef<Blob[]>([])
  const activeMimeTypeRef = useRef(DEFAULT_RECORDER_MIME)
  const uploadingRef = useRef(false)
  const lastUploadedChunkIndexRef = useRef(0)
  const uploadBatchIndexRef = useRef(0)
  const absoluteBaseRef = useRef(0)

  // Internal state exposed
  const [uploading, setUploading] = useState(false)
  const [uploadCount, setUploadCount] = useState(0)
  const [idbWarning, setIdbWarning] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable refs
  const doChunkUpload = useCallback(
    async (isFinal: boolean): Promise<boolean> => {
      if (provisionalPendingRef.current) return false
      if (uploadingRef.current && !isFinal) return false
      if (isFinal) {
        await uploadLockRef.current.catch(() => {})
      }

      const newChunks = chunksRef.current.slice(lastUploadedChunkIndexRef.current)
      if (newChunks.length === 0 && !isFinal) return true
      const batchHighWatermark = lastUploadedChunkIndexRef.current + newChunks.length

      uploadingRef.current = true
      setUploading(true)

      let resolveLock!: (v: boolean) => void
      uploadLockRef.current = new Promise<boolean>((resolve) => {
        resolveLock = resolve
      })

      const blob = new Blob(newChunks, { type: activeMimeTypeRef.current })
      const batchIndex = uploadBatchIndexRef.current
      const retryDelays = [1_000, 3_000, 10_000]
      let lastError: unknown

      // The first upload (batchIndex 0) carries the remote channel's capture-time
      // offset so the backend can write the {id}.remote.offset sidecar.
      const offsetSec = batchIndex === 0 ? offsetSecRef?.current : undefined

      for (let attempt = 0; attempt < retryDelays.length; attempt++) {
        try {
          const receipt = await uploadChunk(meetingId, blob, batchIndex, isFinal, channel, offsetSec)
          if (!receipt.accepted) {
            throw new Error(`Server rejected chunk ${batchIndex} (accepted=false)`)
          }
          reportSuccess()
          // Persist to IDB BEFORE trimming from RAM — once trimmed, these chunks
          // exist nowhere client-side, and the local gold copy must stay complete
          // until the meeting is summarized. Best-effort: the server holds the
          // bytes either way, so an IDB failure only degrades local redundancy.
          try {
            await saveChunksToIDB(meetingId, chunksRef.current, startTimeRef.current, absoluteBaseRef.current, {
              isProvisional: provisionalPendingRef.current,
              subject,
              templateId,
              channel,
              offsetSec: offsetSecRef?.current,
              uploadBatchIndex: batchIndex + 1,
            })
          } catch {
            setIdbWarning(true)
          }
          chunksRef.current = chunksRef.current.slice(batchHighWatermark)
          absoluteBaseRef.current += batchHighWatermark
          lastUploadedChunkIndexRef.current = 0
          uploadBatchIndexRef.current = batchIndex + 1
          setUploadCount((c) => c + 1)
          setError("")
          uploadingRef.current = false
          setUploading(false)
          resolveLock(true)
          return true
        } catch (err) {
          lastError = err
          if (attempt < retryDelays.length - 1) {
            // Back off between retries for periodic uploads too — burning all
            // three attempts with zero delay turns a momentary network blip
            // into an instant failure.
            await new Promise((r) => setTimeout(r, retryDelays[attempt]))
          }
        }
      }

      reportFailure()
      if (isOnlineRef.current) {
        setError(`Upload failed: ${extractErrorMessage(lastError)}. Recording is saved locally.`)
      }
      uploadingRef.current = false
      setUploading(false)
      resolveLock(false)
      return false
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs (see biome-ignore above)
    [meetingId, reportSuccess, reportFailure, setError, channel, subject, templateId],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable refs
  const startPeriodicSave = useCallback(() => {
    saveTimerRef.current = setInterval(async () => {
      if (chunksRef.current.length > 0) {
        try {
          await saveChunksToIDB(meetingId, chunksRef.current, startTimeRef.current, absoluteBaseRef.current, {
            isProvisional: provisionalPendingRef.current,
            subject,
            templateId,
            channel,
            // Remote-only; undefined for the room channel (omitted server-side).
            offsetSec: offsetSecRef?.current,
            uploadBatchIndex: uploadBatchIndexRef.current,
          })
          if (!idbOkRef.current) {
            idbOkRef.current = true
            setIdbWarning(false)
          }
        } catch {
          if (idbOkRef.current) {
            idbOkRef.current = false
            setIdbWarning(true)
            doChunkUpload(false).catch(() => {})
          }
        }
      }
    }, 5000)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs (see biome-ignore above)
  }, [meetingId, doChunkUpload, subject, templateId, channel])

  const stopPeriodicSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearInterval(saveTimerRef.current)
      saveTimerRef.current = null
    }
  }, [])

  const startChunkUploadTimer = useCallback(() => {
    chunkUploadTimerRef.current = setInterval(async () => {
      await doChunkUpload(false)
    }, CHUNK_UPLOAD_INTERVAL_MS)
  }, [doChunkUpload])

  const stopChunkUploadTimer = useCallback(() => {
    if (chunkUploadTimerRef.current) {
      clearInterval(chunkUploadTimerRef.current)
      chunkUploadTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current)
      if (chunkUploadTimerRef.current) clearInterval(chunkUploadTimerRef.current)
    }
  }, [])

  return {
    chunksRef,
    activeMimeTypeRef,
    uploadingRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    uploading,
    uploadCount,
    setUploadCount,
    idbWarning,
    setIdbWarning,
    doChunkUpload,
    startPeriodicSave,
    stopPeriodicSave,
    startChunkUploadTimer,
    stopChunkUploadTimer,
  }
}
